// Web implementation of the product tour. Mirrors the native TourProvider's
// state machine (welcome → chat → [drawer-icon on narrow viewports] →
// sidebar walk → complete) but renders a DOM overlay (TourOverlay.web) and
// resolves targets by querying live elements instead of RN refs.
//
// Trigger contract is identical to native: the post-onboarding welcome gate
// (mounted app-wide in (app)/_layout.web.tsx) fires requestTourStart() when
// dismissed, maybeAutoStart starts the tour the first time a user who has
// not seen it opens chat (never by moving them there: onboarding ends on the
// CV, and the tour is a coach mark for the chat surface), and finish()
// consumes the post-tour destination a welcome redirect parked so whoever was
// headed somewhere else lands there once the tour ends (completed or skipped).
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { View } from 'react-native';
import { usePathname, useRouter, useSegments } from 'expo-router';
import { useAuth } from '../../hooks/useAuth';
import { useLocale } from '../../hooks/useLocale';
import { trackProductEvent } from '../../lib/product-analytics';
import { hasSeenTour, markTourSeen } from '../../lib/tour-persistence';
import { consumePostTourDestination } from '../../lib/post-tour-destination';
import { isTourSurfaceRoute } from '../../lib/tour-surface';
import { shouldShowPostOnboardingWelcome } from '../../lib/post-onboarding-welcome';
import { TourContext } from './useTour';
import { emitTourAction } from './tour-actions';
import { subscribeTourStartRequest } from './tour-bootstrap';
import { TourOverlay } from './TourOverlay.web';
import {
  WEB_SIDEBAR_CAPTIONS,
  getSidebarCaptionElement,
  type WebSidebarCaption,
} from './web-tour-targets';
import type { TourContextValue, TourStep, TourTargetId } from './types';

// Below this width the sidebar is an off-canvas drawer (ProtectedWebShell's
// breakpoint): the tour then includes the hamburger step like native.
const MOBILE_QUERY = '(max-width: 767px)';
const AUTO_START_DELAY_MS = 350;
const REQUESTED_START_DELAY_MS = 180;

function isMobileViewport() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(MOBILE_QUERY).matches;
}

function getWebStepOrder(): TourStep[] {
  return isMobileViewport()
    ? ['welcome', 'chat', 'drawer-icon', 'drawer-items', 'complete']
    : ['welcome', 'chat', 'drawer-items', 'complete'];
}

export function TourProvider({
  children,
  autoStart = true,
}: {
  children: ReactNode;
  /** False while the workspace is still resolving: a company user is about
   * to be redirected, so the seeker tour must not schedule itself yet. */
  autoStart?: boolean;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const segments = useSegments();
  const segmentsKey = segments.join('/');
  const { user, isAuthenticated, isOnboardingCompleted } = useAuth();
  const { locale } = useLocale();

  const [currentStep, setCurrentStep] = useState<TourStep | null>(null);
  const currentStepRef = useRef<TourStep | null>(null);
  const [drawerCaptionIndex, setDrawerCaptionIndex] = useState(0);
  const drawerCaptionIndexRef = useRef(0);
  const [captions, setCaptions] = useState<WebSidebarCaption[]>(
    WEB_SIDEBAR_CAPTIONS,
  );
  const captionsRef = useRef<WebSidebarCaption[]>(WEB_SIDEBAR_CAPTIONS);
  const stepOrderRef = useRef<TourStep[]>(getWebStepOrder());
  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const firstMessageReportedRef = useRef(false);
  const autoStartedForUserRef = useRef<string | null>(null);
  const lastStartAtRef = useRef(0);

  const eventProps = useMemo(
    () => ({
      platform: 'web' as const,
      persona: 'seeker',
      locale,
    }),
    [locale],
  );

  const updateStep = useCallback((nextStep: TourStep | null) => {
    currentStepRef.current = nextStep;
    setCurrentStep(nextStep);
  }, []);

  const updateCaptionIndex = useCallback((index: number) => {
    drawerCaptionIndexRef.current = index;
    setDrawerCaptionIndex(index);
  }, []);

  const trackStepViewed = useCallback(
    (step: TourStep, index: number) => {
      trackProductEvent('tour_step_viewed', {
        ...eventProps,
        step,
        index,
      });
    },
    [eventProps],
  );

  const clearScheduledStart = useCallback(() => {
    if (startTimerRef.current) {
      clearTimeout(startTimerRef.current);
      startTimerRef.current = null;
    }
  }, []);

  const finish = useCallback(
    (reason: 'complete' | 'skip', skippedStep?: TourStep) => {
      markTourSeen(user?.id);
      emitTourAction('close-drawer');

      if (reason === 'skip' && skippedStep) {
        trackProductEvent('tour_step_skipped', {
          ...eventProps,
          step: skippedStep,
        });
      }

      if (reason === 'complete') {
        trackProductEvent('tour_completed', eventProps);
      }

      updateStep(null);
      updateCaptionIndex(0);

      // Whatever destination the tour displaced - the job a public "Apply"
      // parked during onboarding, onboarding's "Find jobs" landing, or the
      // route this user opened the app on - is restored here, whether the tour
      // was completed or skipped.
      const postTourDestination = consumePostTourDestination();
      if (postTourDestination) {
        router.replace(postTourDestination as never);
      }
    },
    [eventProps, router, updateCaptionIndex, updateStep, user?.id],
  );

  const start = useCallback(() => {
    if (currentStepRef.current !== null) return;
    const now = Date.now();
    // Hard throttle: ignore re-entries within 1.5s of the last start -
    // welcome-gate dismissal and maybeAutoStart can race after navigation.
    if (now - lastStartAtRef.current < 1500) return;
    lastStartAtRef.current = now;
    firstMessageReportedRef.current = false;
    stepOrderRef.current = getWebStepOrder();
    updateCaptionIndex(0);
    updateStep('welcome');
    trackProductEvent('tour_started', eventProps);
    trackStepViewed('welcome', 0);
  }, [eventProps, trackStepViewed, updateCaptionIndex, updateStep]);

  const goTo = useCallback(
    (step: TourStep) => {
      if (step === 'complete') {
        finish('complete');
        return;
      }

      if (step === 'drawer-items') {
        // Only walk rows that are actually in the DOM for this layout; the
        // shell expands/opens the sidebar so labels are visible.
        emitTourAction('open-drawer');
        const available = WEB_SIDEBAR_CAPTIONS.filter(
          (caption) =>
            getSidebarCaptionElement(caption) !== null ||
            document.querySelector(caption.selector) !== null,
        );
        const nextCaptions = available.length
          ? available
          : WEB_SIDEBAR_CAPTIONS;
        captionsRef.current = nextCaptions;
        setCaptions(nextCaptions);
        updateCaptionIndex(0);
      }

      const index = stepOrderRef.current.indexOf(step);
      updateStep(step);
      trackStepViewed(step, index === -1 ? 0 : index);
    },
    [finish, trackStepViewed, updateCaptionIndex, updateStep],
  );

  const next = useCallback(() => {
    const step = currentStepRef.current;
    const order = stepOrderRef.current;
    const idx = step ? order.indexOf(step) : -1;

    if (idx === -1) return;

    if (step === 'drawer-items') {
      const nextCaption = drawerCaptionIndexRef.current + 1;
      if (nextCaption < captionsRef.current.length) {
        updateCaptionIndex(nextCaption);
        return;
      }
      finish('complete');
      return;
    }

    const nextStep = order[idx + 1];
    if (!nextStep) {
      finish('complete');
      return;
    }
    goTo(nextStep);
  }, [finish, goTo, updateCaptionIndex]);

  const skip = useCallback(() => {
    const step = currentStepRef.current;
    if (!step) return;
    finish('skip', step);
  }, [finish]);

  const registerTargetRef = useCallback(
    (_id: TourTargetId, _ref: View | null) => {},
    [],
  );

  const getTargetRef = useCallback((_id: TourTargetId) => null, []);

  const notifyDrawerOpened = useCallback(() => {
    const step = currentStepRef.current;
    if (!step) return;
    if (step === 'drawer-items' || step === 'complete') return;

    trackProductEvent('tour_drawer_opened_during_tour', {
      ...eventProps,
      step,
    });
    goTo('drawer-items');
  }, [eventProps, goTo]);

  const notifyFirstMessageSent = useCallback(() => {
    if (firstMessageReportedRef.current) return;
    if (currentStepRef.current === null) return;
    firstMessageReportedRef.current = true;
    trackProductEvent('tour_first_message_sent', {
      ...eventProps,
      step: currentStepRef.current,
    });
  }, [eventProps]);

  const maybeAutoStart = useCallback(() => {
    if (!autoStart) {
      clearScheduledStart();
      return;
    }
    if (!isAuthenticated || !isOnboardingCompleted) return;
    if (!user?.id) return;
    if (autoStartedForUserRef.current === user.id) return;
    if (currentStepRef.current !== null) return;
    // Defer to the welcome flow first; it will signal via requestTourStart().
    if (shouldShowPostOnboardingWelcome(user.id)) return;

    if (hasSeenTour(user.id)) {
      autoStartedForUserRef.current = user.id;
      return;
    }

    // The tour's targets only exist on chat, and it is a first-visit coach
    // mark for that surface, never a detour: a user who lands elsewhere
    // (onboarding ends on their CV) keeps the screen they are on, and the
    // tour starts the first time they open chat (this callback re-runs on
    // every route change).
    if (!isTourSurfaceRoute(pathname, segmentsKey)) {
      clearScheduledStart();
      return;
    }

    clearScheduledStart();
    startTimerRef.current = setTimeout(() => {
      startTimerRef.current = null;
      if (autoStartedForUserRef.current === user.id) return;
      autoStartedForUserRef.current = user.id;
      start();
    }, AUTO_START_DELAY_MS);
  }, [
    autoStart,
    clearScheduledStart,
    isAuthenticated,
    isOnboardingCompleted,
    pathname,
    segmentsKey,
    start,
    user?.id,
  ]);

  useEffect(() => {
    maybeAutoStart();
  }, [maybeAutoStart]);

  useEffect(() => {
    return subscribeTourStartRequest(() => {
      if (!user?.id) return;
      if (currentStepRef.current !== null) return;
      autoStartedForUserRef.current = user.id;
      clearScheduledStart();
      startTimerRef.current = setTimeout(() => {
        startTimerRef.current = null;
        start();
      }, REQUESTED_START_DELAY_MS);
    });
  }, [clearScheduledStart, start, user?.id]);

  useEffect(() => () => clearScheduledStart(), [clearScheduledStart]);

  const value = useMemo<TourContextValue>(
    () => ({
      currentStep,
      isActive: currentStep !== null,
      drawerCaptionIndex,
      start,
      next,
      skip,
      goTo,
      registerTargetRef,
      getTargetRef,
      notifyDrawerOpened,
      notifyFirstMessageSent,
    }),
    [
      currentStep,
      drawerCaptionIndex,
      getTargetRef,
      goTo,
      next,
      notifyDrawerOpened,
      notifyFirstMessageSent,
      registerTargetRef,
      skip,
      start,
    ],
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      {currentStep !== null && currentStep !== 'complete' ? (
        <TourOverlay
          step={currentStep}
          captions={captions}
          captionIndex={drawerCaptionIndex}
          onNext={next}
          onSkip={skip}
        />
      ) : null}
    </TourContext.Provider>
  );
}
